/**
 * Fixed-timestep game loop.
 *
 * Simulation always advances in whole steps of exactly TICK_SECONDS, no matter what
 * frame rate the device runs at, so a phone at 30fps and a desktop at 144fps agree on
 * how far a bunny hops. Rendering happens once per animation frame and receives an
 * `alpha` in [0,1] describing how far between the last two simulation steps it sits,
 * so movement stays smooth even though physics is quantised.
 */

/** 60 simulation steps per second. */
export const TICK_HZ = 60
export const TICK_SECONDS = 1 / TICK_HZ

/**
 * Never simulate more than this much wall-clock time in one frame. Without the clamp,
 * a backgrounded tab returns with a multi-second debt and the loop tries to catch up
 * in one blocking burst ("spiral of death"). Time past the clamp is simply dropped.
 */
const MAX_FRAME_SECONDS = 0.25

export interface LoopHandlers {
  /** Advance the simulation by exactly `dt` seconds. Called 0..n times per frame. */
  update(dt: number): void
  /** Draw. `alpha` is the interpolation factor between the previous and current step. */
  render(alpha: number): void
}

export class GameLoop {
  private readonly handlers: LoopHandlers
  private accumulator = 0
  private lastTime = 0
  private frameId: number | null = null

  /** Wall-clock seconds the simulation has run. Advances only in whole ticks. */
  simulatedTime = 0

  constructor(handlers: LoopHandlers) {
    this.handlers = handlers
  }

  start(): void {
    if (this.frameId !== null) return
    this.lastTime = performance.now()
    this.accumulator = 0
    this.frameId = requestAnimationFrame(this.frame)
  }

  stop(): void {
    if (this.frameId === null) return
    cancelAnimationFrame(this.frameId)
    this.frameId = null
  }

  /**
   * Advance the simulation by `seconds` of wall-clock time and render once, using the
   * exact same accumulator path as the rAF loop. Exists so the loop can be driven from
   * a test harness — headless/background browser tabs never fire requestAnimationFrame.
   */
  advance(seconds: number): void {
    this.accumulator += Math.min(seconds, MAX_FRAME_SECONDS)
    while (this.accumulator >= TICK_SECONDS) {
      this.handlers.update(TICK_SECONDS)
      this.simulatedTime += TICK_SECONDS
      this.accumulator -= TICK_SECONDS
    }
    this.handlers.render(this.accumulator / TICK_SECONDS)
  }

  private readonly frame = (now: number): void => {
    this.frameId = requestAnimationFrame(this.frame)

    const elapsed = Math.min((now - this.lastTime) / 1000, MAX_FRAME_SECONDS)
    this.lastTime = now
    this.accumulator += elapsed

    while (this.accumulator >= TICK_SECONDS) {
      this.handlers.update(TICK_SECONDS)
      this.simulatedTime += TICK_SECONDS
      this.accumulator -= TICK_SECONDS
    }

    this.handlers.render(this.accumulator / TICK_SECONDS)
  }
}
